import { useIntl } from "react-intl";
import { mergeClasses, tokens } from "@fluentui/react-components";
import { SquareToggleButton } from "@/components/atoms/squareToggleButton";
import { Flex } from "@/components/atoms/flex";
import { PopoverView } from "@/components/atoms/popover";
import { Callback18Regular, ColdTransferToQueue20Regular } from "@/common/icons";
import { ArrowForward20Regular, Delete20Regular } from "@fluentui/react-icons";
import { Handled15Regular } from "@/common/icons/handledIcon/handledIcon";
import { Unhandled15Regular } from "@/common/icons/unhandledIcon/unhandledIcon";
import {
	CurrentInteractionActions,
	CurrentInteractionState,
	NewInteraction,
	NewInteractionData,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { InteractionType } from "@/common/types";
import { useVoiceOutbound } from "@/hooks/outbound";
import {
	SupervisorState,
	useSupervisorStore,
} from "@/common/stores/nonpersistingStores/supervisor";
import { ConfirmationDialog } from "@/components/atoms/dialog/ConfirmationDialog";
import { useCallback, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
	deleteVoicemail,
	handleVoicemail,
} from "@/repository/queryFns/voicemails";
import { useMessage } from "@/common/messages";
import { useAgentPermissionsStore } from "@/common/stores/persistingStores/agentPermissions/agentPermissionsStore";
import { AgentPermissionsState } from "@/common/stores/persistingStores/agentPermissions";
import { addBreadcrumb, captureError } from "@/common/helpers/sentryHelper";
import { isUnknownCustomerContact } from "@/common/helpers";
import { useInteractionBarCommonStyle } from "@/common/style/interactionBarCommonStyle";
import { TargetSelectionSource } from "@/repository/models/outboundCall";
import {
	NavigationActions,
	useNavigationStore,
	ToggleTabType,
} from "@/common/stores/persistingStores/navigation";
import { AppCacheActions, CacheKey, useAppCacheStore } from "@/common/stores/persistingStores/app";
import { InteractionPopoverType, TransferType, TransferChannelType } from "@/common/types/transfer";
import { InteractionTransferToAgentPopover } from "@/components/organisms/InteractionTransferToAgent";
import { InteractionTransferIvrPopover } from "@/components/organisms/interactionTransferToIvr/InteractionTransferIvrPopover";

export const VoicemailInProgressActionButtons = () => {
	const style = useInteractionBarCommonStyle();
	const intl = useIntl();
	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState & CurrentInteractionActions) => state
	);
	const {
		isOutboundPossible,
		isOutboundAllowed,
		hasAnyOutboundRightEndpoints,
	} = useVoiceOutbound();
	
	//#warning-ts: check if isSupervisor is needed; I don't see how these buttons can be displayed if not having isActive true
	const isSupervisor = useSupervisorStore(
		(state: SupervisorState) => state.isSupervisor
	);
	//#warning-ts: check if isActive is needed; I don't see how these buttons can be displayed if the user is not active
	const isActive = useAgentPermissionsStore(
		(state: AgentPermissionsState) => state.isActive
	);
	const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
		useState<boolean>(false);
	const [interactionPopoverType, setInteractionPopoverType] =
		useState<InteractionPopoverType | null>(null);
	const { show: showMessage } = useMessage();

	const toggleInteractionPopoverOpening = useCallback(
		(type: InteractionPopoverType) => {
			setInteractionPopoverType((oldState) => (oldState === type ? null : type));
		},
		[setInteractionPopoverType]
	);

	const setToggledTab = useNavigationStore(
		(state: NavigationActions) => state.setToggledTab
	);

	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);

	const callbackTarget = currentInteraction.active?.contactInformation ?? "";

	const handleOnCallClick = () => {
		addBreadcrumb("ui_interaction", "Initiating outbound from voicemail", {
			...currentInteraction,
			isSupervisor,
		});

		const newInteractionData: NewInteractionData = {
			name: currentInteraction.active?.name || "",
			value: callbackTarget,
			selectionSource: TargetSelectionSource.VoicemailInProgress,
			selectionInteractionHistoryId: currentInteraction.active?.id,
		};

		var newInteraction: NewInteraction = {
			type: InteractionType.Call,
			data: newInteractionData,
		};

		appStoreActions.setCache(CacheKey.OutboundType, InteractionType.Call);

		setToggledTab(ToggleTabType.NewInteraction);
		currentInteraction.showNew(newInteraction);
	};

	const deleteVoicemailMutation = useMutation({
		mutationFn: () => deleteVoicemail(currentInteraction.active!.id),
		onSuccess: (_: any) => {
			showMessage("global", {
				intent: "success",
				text: intl.formatMessage({
					id: "Interaction.Voicemails.DeletedVoicemail",
				}),
			});
		},
		onError: (err: any, data: any, _context: any) => {
			captureError(
				err,
				"Error deleting voicemail interaction",
				undefined,
				data
			);
			console.error(err);
		},
	});

	const handleVoicemailMutation = useMutation({
		mutationFn: (isHandled: boolean) =>
			handleVoicemail(currentInteraction.active!.id, isHandled),
		onError: (err: any, data: any, _context: any) => {
			captureError(
				err,
				"Error handling voicemail interaction",
				undefined,
				data
			);
			console.error(err);
		},
	});

	return (
		<>
			<Flex
				vAlign="center"
				className={mergeClasses(
					style.interactionText,
					style.interactionButtons
				)}
			>
				{isOutboundPossible &&
					isOutboundAllowed &&
					hasAnyOutboundRightEndpoints &&
					!isUnknownCustomerContact(callbackTarget) && (
						<SquareToggleButton
							icon={<Callback18Regular fill={tokens.colorNeutralForeground1} />}
							tooltip={intl.formatMessage({
								id: "Interaction.Operations.OutboundCall",
							})}
							onClick={() => handleOnCallClick()}
						/>
					)}
				{(isSupervisor || isActive) && (
					<PopoverView
						trigger={
							<SquareToggleButton
								icon={<ArrowForward20Regular />}
								tooltip={intl.formatMessage({
									id: "Interaction.Buttons.TransferVoicemail",
								})}
								onClick={() => toggleInteractionPopoverOpening(TransferType.VoicemailTransferToAgent)}
							/>
						}
						content={
							<InteractionTransferToAgentPopover
								closePopover={() => toggleInteractionPopoverOpening(TransferType.VoicemailTransferToAgent)}
								channelType={TransferChannelType.Voicemail}
							/>
						}
						positioning={{ position: "below" }}
						open={interactionPopoverType === TransferType.VoicemailTransferToAgent}
					/>
				)}
				{(isSupervisor || isActive) && (
					<PopoverView
						trigger={
							<SquareToggleButton
								icon={<ColdTransferToQueue20Regular />}
								tooltip={intl.formatMessage({
									id: "Interaction.Buttons.TransferVoicemailToQueue",
								})}
								onClick={() => toggleInteractionPopoverOpening(TransferType.VoicemailTransferToQueue)}
							/>
						}
						content={
							<InteractionTransferIvrPopover
								closePopover={() => toggleInteractionPopoverOpening(TransferType.VoicemailTransferToQueue)}
								channelType={TransferChannelType.Voicemail}
							/>
						}
						positioning={{ position: "below" }}
						open={interactionPopoverType === TransferType.VoicemailTransferToQueue}
					/>
				)}
				{(isSupervisor || isActive) && (
					<SquareToggleButton
						icon={<Delete20Regular />}
						tooltip={intl.formatMessage({
							id: "Interaction.Buttons.DeleteVoicemail",
						})}
						onClick={() => {
							addBreadcrumb("ui_interaction", "Deleting voicemail", {
								...currentInteraction,
								isSupervisor,
							});
							setIsConfirmationDialogOpen(true);
						}}
					/>
				)}
				<SquareToggleButton
					icon={<Unhandled15Regular strokeColor="#FFFFFF" />}
					tooltip={intl.formatMessage({
						id: "Interaction.Voicemails.MarkUnhandle",
					})}
					className={mergeClasses(style.squareColoredButton, style.redColor)}
					onClick={() => {
						addBreadcrumb("ui_interaction", "Mark voicemail as unhandled", {
							...currentInteraction,
							isSupervisor,
						});
						handleVoicemailMutation.mutate(false);
					}}
				/>
				<SquareToggleButton
					icon={<Handled15Regular strokeColor="#FFFFFF" />}
					tooltip={intl.formatMessage({
						id: "Interaction.Voicemails.MarkHandle",
					})}
					className={mergeClasses(style.squareColoredButton, style.greenColor)}
					onClick={() => {
						addBreadcrumb("ui_interaction", "Mark voicemail as handled", {
							...currentInteraction,
							isSupervisor,
						});
						handleVoicemailMutation.mutate(true);
					}}
				/>
			</Flex>
			<ConfirmationDialog
				open={isConfirmationDialogOpen}
				setOpen={setIsConfirmationDialogOpen}
				text={intl.formatMessage({
					id: "Interaction.Voicemails.VoicemailWillBeDeletedMessage",
				})}
				onConfirm={() => {
					setIsConfirmationDialogOpen(false);
					deleteVoicemailMutation.mutate(undefined);
				}}
				onCancel={() => {
					setIsConfirmationDialogOpen(false);
				}}
			/>
		</>
	);
};
